import * as utils from "./forecast-evolution-utils.js";
import { setDataCache, getFromCache, hasCacheData } from "@js/service/cache.js";
import { restApiDomain, nwccDomain } from "@js/nwcc-domains.js";

// Base URL's
const baseUrl = `${nwccDomain}/awdb`;
const restAPIBase = `${restApiDomain}/`;

/**
 * Loads the JSON file from the endpoint: 'url_'. Returns the loaded
 * json as a javascript object. The loaded data is cached for
 * faster performance.
 *
 * @param {string} url_ => URL to retrive the JSON document from
 */
export async function loadJsonFile(url_) {
    // Check if the data is in the cache
    if (hasCacheData(url_)) {
        // Load the json from the cache
        return getFromCache(url_);
    }

    let result;
    await fetch(url_).then(
        (response) => {
            if (response.ok) {
                result = response.json();
                // Store result in cache
                setDataCache(result, url_);
            } else {
                result = {};
            }
        },
        (rejected) => {
            console.error(rejected);
            result = {};
        },
    );

    return result;
}

/**
 * Retrieves station metadata for stations that have the given element code.
 * @param {String} element
 * @returns
 */
export async function getStationDataFile(element) {
    const url = `${baseUrl}/sitedata/metadata/${element}/metadata.json`;
    return await loadJsonFile(url);
}

/**
 * Retrieves forecast station metadata and associated HUC6 names
 * @returns
 */
export async function getBasinFcstDataFile() {
    let url = `${baseUrl}/basin-defs/site-lists/HUC6_FCST.json`;
    return await loadJsonFile(url);
}

/**
 * Given a network ID and a network, queries the REST API for the full
 * station triplet. If the triplet isn't found , return null.
 *
 * @param {String} ntwkID
 * @param {String} ntwk
 * @returns {String} station triplet, or null.
 */
export async function getTripletForNtwkID(ntwkID, ntwk) {
    const url = `${restAPIBase}stations?stationTriplets=${ntwkID}:*:${ntwk}`;
    const metadata = await loadJsonFile(url);
    if (metadata.length > 0) {
        return metadata[0].stationTriplet;
    } else {
        return null;
    }
}

/**
 * Uses the JSON Factory files to compute the yearly streamflow totals
 * for the gievn triplet and period, using either the MONTHLY duration data
 * or the SEMIMONTHLY duration data (depending on the 'semimonthly' argument).
 *
 * This function returns the total for the provided year, as well as the period
 * of record minimum/maximum total streamflow volume.
 *
 * Returns an array of 'null' if data is unavailable or incomplete.
 *
 * @param {String} triplet
 * @param {String} period
 * @param {Integer} year
 * @param {Boolean} semimonthly - set to 'true' if you want to look for semi-monthly stats
 * @returns {Array[number]} - [total_for_year, por_min, por_max]
 */
export async function getJSONFactoryTotalMinMax(triplet, period, year, semimonthly = false) {
    // Try to retrieve the data
    const url = `${baseUrl}/sitedata/${semimonthly ? "SEMIMONTHLY" : "MONTHLY"}/SRVO/${triplet.replaceAll(":", "_")}.json`;
    const data = await loadJsonFile(url);

    // Check for valid dataset
    if (Object.keys(data).length === 0) {
        return {
            total: null,
            min: null,
            max: null,
        };
    }

    // Get the data values from the response
    const values = structuredClone(data.values);

    // Get the relevant start and end dates from the observed data
    const dataStartYear = +data.beginDate.slice(0, 4);
    const dataStartMonth = +data.beginDate.slice(5, 7);
    const dataStartDay = +data.beginDate.slice(8, 10);
    const dataEndMonth = +data.endDate.slice(5, 7);
    const dataEndDay = +data.endDate.slice(8, 10);

    // Prepend and Extend the data values to fill out the first and last year
    // I.e. if dataStartMonth is 7, we need to add 6 null's to the front of the array (assuming monthly)
    // if dataEndMonth is 10, we need to add 2 null's to the end of the array
    let numPrependNaNs = dataStartMonth - 1;
    let numExtendNaNs = 12 - dataEndMonth;

    if (semimonthly) {
        numPrependNaNs = 2 * numPrependNaNs;
        if (dataStartDay > 1) {
            numPrependNaNs += 1;
        }
        numExtendNaNs = 2 * numExtendNaNs;
        if (dataEndDay === 1) {
            numExtendNaNs += 1;
        }
    }

    values.unshift(...Array(numPrependNaNs).fill(null));
    values.push(...Array(numExtendNaNs).fill(null));

    // Get the requested start and end dates from the period
    // Get the indices into the yearly arrays for the defined period
    const [[periodStartMonth, periodStartDay], [periodEndMonth, periodEndDay]] =
        utils.convertPeriodToMonthsAndDays(period);
    const startIdx = semimonthly ? 2 * (periodStartMonth - 1) + (periodStartDay !== 1) : periodStartMonth - 1;
    const endIdx = semimonthly ? 2 * (periodEndMonth - 1) + (periodEndDay > 16) : periodEndMonth - 1;

    // Divide the values array into sets of 12 or 24 (depending on semimonthly);
    // For each chunk, get the appropriate sum for the specified year and forecast period
    const chunkSize = semimonthly ? 24 : 12;
    const summedValues = [];
    const years = [];
    for (let i = 0; i < values.length; i += chunkSize) {
        const chunkedValues = values.slice(i, i + chunkSize);
        const inPeriodValues = chunkedValues.slice(startIdx, endIdx + 1);
        summedValues.push(
            inPeriodValues.reduce((pv, cv) => {
                if (cv) {
                    return pv + cv;
                } else {
                    return NaN;
                }
            }, 0),
        );
        years.push(dataStartYear + +(i / chunkSize));
    }

    // Get the POR min/max
    const min = Math.min(...summedValues.filter((v) => !isNaN(v)));
    const max = Math.max(...summedValues.filter((v) => !isNaN(v)));

    // Get the yearly total if it exists
    let total = null;
    if (years.includes(year)) {
        total = summedValues[years.indexOf(year)];
    }

    return {
        total: total || null,
        min: min || null,
        max: max || null,
    };
}

/**
 * Retrieves the total observation for the given year, period,
 * and forecast station from the JSON factory. Also retrives the min, max, and median
 * data from the JSON factory for the given triplet and period.
 * This can be a different number from the summation of the partial observed data, (but hopefully
 * not too different... )
 * @param {String} triplet
 * @param {String} period
 * @param {integer} year
 */
export async function getObservedStats(triplet, period, year) {
    // Get total observed data and period of record stats.
    let { total, min, max } = await getJSONFactoryTotalMinMax(triplet, period, year, period.includes("16"));

    // Get median data
    let url = `${baseUrl}/data/SRVO/MONTHLY/PERIODMED/SRVO_MONTHLY_PERIODMED_ALL.json`;
    let data = await loadJsonFile(url);
    let median;
    if (Object.keys(data).includes(triplet)) {
        data = data[triplet];
        if (Object.keys(data).includes(period)) {
            median = data[period];
        }
    }

    // Get average data
    url = `${baseUrl}/data/SRVO/MONTHLY/PERIODAVG/SRVO_MONTHLY_PERIODAVG_ALL.json`;
    data = await loadJsonFile(url);
    let avg;
    if (Object.keys(data).includes(triplet)) {
        data = data[triplet];
        if (Object.keys(data).includes(period)) {
            avg = data[period];
        }
    }

    // check for undefined/null values.
    if (!total) total = NaN;
    if (!median) median = NaN;
    if (!max) max = NaN;
    if (!min) min = NaN;
    if (!avg) avg = NaN;

    return {
        total: total,
        median: median,
        min: min,
        max: max,
        avg: avg,
    };
}

/**
 * Returns an object containing semi-monthly or monthly data for the semi-months or months
 * within the provided period and year for the given triplet. The object
 * has the structure:
 *      {
 *          '2020-04-01': 4434,
 *          '2020-04-16': 5231,
 *          ...
 *          '2020-07-16': 612
 *      }
 *
 * @param {String} triplet
 * @param {String} period
 * @param {Integer} year
 * @returns object
 */
export async function getPartialObservedJSON(triplet, period, year, semimonthly = false) {
    const [[startMonth, startDay]] = utils.convertPeriodToMonthsAndDays(period);
    const periodStartDate = new Date(
        `${year}-${startMonth.toString().padStart(2, "0")}-${startDay.toString().padStart(2, "0")}`,
    );
    const dates = [];
    const values = [];

    // Get semi-monthly json file
    const url = `${baseUrl}/sitedata/${semimonthly ? "SEMIMONTHLY" : "MONTHLY"}/SRVO/${triplet.replaceAll(":", "_")}.json`;
    let data = await loadJsonFile(url);

    // check that file isn't empty
    if (Object.keys(data).length === 0) {
        return null;
    }

    // Make sure data includes requested year
    let currentDate = new Date(data.beginDate);
    const lastDate = new Date(data.endDate);
    if (periodStartDate >= currentDate && periodStartDate < lastDate) {
        // Store the values
        for (const value of data.values) {
            if (utils.isDateInPeriod(currentDate, period, year) && !isNaN(value) && value !== null) {
                dates.push(currentDate);
                values.push(value);
            }
            currentDate = semimonthly ? currentDate.addSemiMonths(1) : currentDate.addMonths(1);
        }

        // return data as an object
        data = new (function () {
            dates.forEach((date, index) => {
                this[date.toISOString().substring(0, 10)] = values[index];
            });
        })();
        return data;
    }

    return null;
}

/**
 *
 * Retrieves daily data from the AWDB REST Service and creates a daily
 * SRVO dataset. The daily SRVO dataset is computed as the sum of
 * terms in the streamflow adjusment equation.
 *
 * @param {*} triplet
 * @param {*} period
 * @param {*} year
 * @param {*} elementCode
 * @param {*} sacEqn
 * @returns an object with the form {
 *  'YYYY-MM-DD': <flow value in AF>,
 *  'YYYY-MM-DD': <...>
 * }
 */
export async function getPartialObservedRest(triplet, period, year, elementCode, sacEqn = null) {
    const elementMap = {
        DIV: "DIVD",
        SRVOO: "SRDOO",
        SRVOX: "SRDOX",
        RESC: "RESC",
    };

    const [[startMonth, startDay], [endMonth, endDay]] = utils.convertPeriodToMonthsAndDays(period);
    const periodStartDate = new Date(
        `${year}-${startMonth.toString().padStart(2, "0")}-${startDay.toString().padStart(2, "0")}T00:00`,
    );
    let periodEndDate = new Date(
        `${year}-${endMonth.toString().padStart(2, "0")}-${endDay.toString().padStart(2, "0")}T00:00`,
    );
    const today = new Date().addDays(-1);
    periodEndDate = periodEndDate < today ? periodEndDate : today;

    // INitialize an empty data object
    const data = new (function () {
        let currentDate = periodStartDate;
        while (currentDate <= periodEndDate) {
            this[currentDate.toISOString().substring(0, 10)] = sacEqn ? 0 : NaN;
            currentDate = currentDate.addDays(1);
        }
    })();

    // Check if the data needs to be derived from a SAC equation
    if (sacEqn) {
        // Iterate thru the SAC equation terms and create the composite dataset
        for (const term of sacEqn.terms) {
            const termBeginDate = new Date(
                term.beg_date.toLowerCase() === "always" ? "1795-01-01T00:00" : term.beg_date + "T00:00",
            );
            const termEndDate = new Date(
                term.end_date.toLowerCase() === "always" ? "2150-01-01T00:00" : term.end_date + "T00:00",
            );
            const termElement = elementMap[term.ele_cd];
            const termData = await getPartialObservedRest(`${term.ntwk_sta_id}:*:*`, period, year, termElement);
            if (!termData) {
                // triplet not found
                return null;
            }
            Object.keys(termData).forEach((date) => {
                const jsDt = new Date(date + "T00:00");
                const sgn = term.type_lbl === "+" ? 1 : -1;

                if (jsDt >= termBeginDate && jsDt <= termEndDate) {
                    data[date] = data[date] + sgn * termData[date];
                }
            });
        }
        return data;

        // Otherwise, get the daily data from the REST API for the equation term.
    } else {
        // Figure out the triplet if it's not enitrely  provided
        if (triplet.includes("*")) triplet = await getTripletForNtwkID(triplet.split(":")[0], triplet.split(":")[2]);
        if (!triplet) {
            return null;
        }

        // Get daily data for the element and triplet
        const url =
            `${restAPIBase}/data?stationTriplets=${triplet}` +
            `&elements=${elementCode}&duration=DAILY` +
            `&beginDate=${periodStartDate.addDays(-1).toISOString().substring(0, 10)}` +
            `&endDate=${periodEndDate.toISOString().substring(0, 10)}`;
        const obsData = await loadJsonFile(url);

        // Process the data if available
        const obs = obsData?.[0]?.data?.[0];
        if (!obs) return data;
        const values = obs.values;
        let lastValue = values[0].value;
        const multiplier = obs.stationElement.storedUnitCode === "ac_ft" ? 1 : 86400 / 43560;
        if (values.length > 0) {
            values.forEach((datapoint) => {
                const dt = new Date(datapoint.date + "T00:00");
                const val = datapoint.value;
                if (dt >= periodStartDate && dt <= periodEndDate) {
                    if (elementCode === "RESC") {
                        data[datapoint.date] = (val - lastValue) * multiplier;
                        lastValue = val;
                    } else {
                        data[datapoint.date] = val * multiplier;
                    }
                }
            });
        }
        return data;
    }
}

export function getValidForecastsForEachPubDate(candidateForecasts, period) {
    const newForecastList = [];

    // Get a list of sorted unique publication
    const pubDates = [...new Set(candidateForecasts.map((f) => f.publicationDate))];
    pubDates.sort((a, b) => {
        return a.localeCompare(b);
    });

    // Iterate over pub dates and explicitely choose the best forecast
    for (const pubDate of pubDates) {
        // candidate forecasts for pub date
        let fcsts = candidateForecasts.filter((f) => f.publicationDate == pubDate);

        // Only look at SRVO forecasts
        fcsts = fcsts.filter((f) => f.elementCode === "SRVO");

        // Only consider forecasts that are valid for mixed-past processing.
        fcsts = fcsts.filter((f) => {
            let [fcstPeriodStart, fcstPeriodEnd] = f.forecastPeriod;
            fcstPeriodStart = fcstPeriodStart.split("-").map((p) => {
                return +p;
            });
            fcstPeriodStart = utils.convertMonthAndDayToPeriodLabel(...fcstPeriodStart);
            fcstPeriodEnd = fcstPeriodEnd.split("-").map((p) => {
                return +p;
            });
            fcstPeriodEnd = utils.convertMonthAndDayToPeriodLabel(...fcstPeriodEnd);
            return utils.isValidMixedPastPeriod(`${fcstPeriodStart}-${fcstPeriodEnd}`, period);
        });

        newForecastList.push(...fcsts);
    }

    return newForecastList;
}

/**
 *
 * @param {*} forecasts
 * @returns
 */
export function convertForecastsToAcreFeet(forecasts) {
    for (const forecast of forecasts) {
        if (forecast.unitCode === "kac_ft") {
            for (const key of Object.keys(forecast.forecastValues)) {
                forecast.forecastValues[key] *= 1000;
            }
        }
    }
    return forecasts;
}

/**
 *
 * @param {*} triplet
 * @param {*} period
 * @param {*} year
 * @param {*} statusList
 * @returns {Array} -> an array of forecasts in the form:
 *  [
 *      {pubDate: '2020-01-01', 'period': 'APR-JUL', fcstStatus: 'raw', exceedData: {10: ...,}},
 *      {pubDate: '2020-01-01', 'period': 'APR-JUL', fcstStatus: 'final', exceedData: {10: ...,}}
 *  ]
 */
export async function getForecasts(triplet, period, year, statusList = ["final"]) {
    // Initialize output
    const fcstData1 = [];

    for (const status of statusList) {
        // Retrieve the forecasts for a given status
        const url =
            `${restAPIBase}forecasts?stationTriplets=${triplet}&beginPublicationDate=${year}-01-01` +
            `&endPublicationDate=${year}-09-30&forecastStatus=${status}`;
        let fcsts = await loadJsonFile(url);

        // Check for available foreacsts
        if (fcsts.length > 0) {
            fcsts = fcsts[0];

            if (fcsts.data.length > 0) {
                let f = structuredClone(fcsts.data);
                f = getValidForecastsForEachPubDate(f, period);
                f = convertForecastsToAcreFeet(f);
                fcstData1.push(...f);
            }
        }
    }

    return fcstData1;
}

/**
 *
 * @param {*} triplet
 * @returns {Object} {periods: list of periods, primary_period: most common period}
 */
export async function getPeriodsForTriplet(triplet) {
    // Get published forecasts
    const url = `${baseUrl}/sitedata/SEMIMONTHLY/FCST/${triplet.replaceAll(":", "_")}.json`;
    const data = await loadJsonFile(url);

    if (!data || Object.keys(data).length === 0) return { periods: [], primaryPeriod: null };

    // Initialize variables to keep track of periods
    const periods = [];
    let primaryPeriod = null;
    const periodCounts = {};

    Object.keys(data).forEach((key) => {
        Object.keys(data[key]).forEach((period) => {
            // Convert single month periods to more explicit format
            if (!period.includes("-")) period = `${period}-${period}`;

            if (periodCounts.hasOwnProperty(period)) {
                periodCounts[period]++;
            } else {
                periodCounts[period] = 1;
            }
            if (!periods.includes(period)) {
                periods.push(period);
            }
        });
    });

    // Figure out primary period by using the period counts
    primaryPeriod = Object.keys(periodCounts)
        .map((p) => [p, periodCounts[p]])
        .sort((a, b) => {
            return a[1] - b[1];
        })
        .reverse();
    primaryPeriod = primaryPeriod[0][0];

    return { periods: periods, primaryPeriod: primaryPeriod };
}

/**
 *
 * @param {*} triplet
 * @param {*} period
 * @param {*} statusList
 * @returns
 */
export async function getYearsForPeriodAndTriplet(triplet, period, statusList = ["final"]) {
    // Initialize variables
    let years = [];

    // Convert period to month/day format
    let [periodStart, periodEnd] = utils.convertPeriodToMonthsAndDays(period);
    periodStart = periodStart.map((s) => s.toString().padStart(2, "0")).join("-");
    periodEnd = periodEnd.map((s) => s.toString().padStart(2, "0")).join("-");

    // iterate over requested statuses and look for forecasts
    for (const status of statusList) {
        const url =
            restAPIBase +
            `forecasts?stationTriplets=${triplet}&elementCodes=SRVO` +
            `&beginPublicationDate=1900-01-01` +
            `&exceedenceProbabilities=50&forecastPeriods=${periodStart},${periodEnd}` +
            `&forecastStatus=${status}`;
        const data = await loadJsonFile(url);

        if (data?.[0]) {
            const allYears = data[0].data.map((f) => +f.publicationDate.slice(0, 4));
            years.push(...allYears);
        }
    }

    // Get the sorted list of years
    years = [...new Set(years)];
    years = years
        .toSorted((a, b) => {
            return +a - +b;
        })
        .reverse();
    return years;
}

export function accumulateObservations(observedData, resolution) {
    // Get list of dates and list of corresponding values
    const dates = Array.from(Object.keys(observedData)).sort((a, b) => a.localeCompare(b));
    const values = dates.map((date) => observedData[date]);

    // Initialize output
    const accumulated = {};
    accumulated[dates[0]] = 0;

    // Iterate over the dates and accumulate
    for (let i = 0; i < dates.length; i++) {
        let shiftedDate;
        const date = dates[i];
        if (resolution === "daily") {
            shiftedDate = new Date(date + "T00:00").addDays(1).toISOString().slice(0, 10);
        } else if (resolution === "semimonthly") {
            shiftedDate = new Date(date + "T00:00").addSemiMonths(1).toISOString().slice(0, 10);
        } else {
            shiftedDate = new Date(date + "T00:00").addMonths(1).toISOString().slice(0, 10);
        }
        accumulated[shiftedDate] = values.slice(0, i + 1).reduce((prev, curr) => prev + curr, 0);
    }

    return accumulated;
}

export function addPartialObserved(forecasts, observations, period) {
    const observationDates = Object.keys(observations).sort((a, b) => a - b);

    for (let i = forecasts.length - 1; i >= 0; i--) {
        const forecast = forecasts[i];

        const { publicationDate, forecastPeriod } = forecast;
        const forecastYear = +publicationDate.slice(0, 4);
        const [forecastPeriodMonth, forecastPeriodDay] = forecastPeriod[0].split("-");

        const [periodMonth, periodDay] = utils.convertPeriodToMonthsAndDays(period)[0];
        const mixedPastPeriodStart = new Date(
            `${forecastYear}-${periodMonth.toString().padStart(2, "0")}-${periodDay.toString().padStart(2, "0")}T00:00`,
        );

        const forecastPeriodStart = new Date(`${forecastYear}-${forecastPeriodMonth}-${forecastPeriodDay}T00:00`);

        // Try and do mixed past processing. If it errors out remove the forecast...
        if (forecastPeriodStart.getTime() > mixedPastPeriodStart.getTime()) {
            // Do we have an accumulated observation for the forecast period start
            const pubDateString = forecastPeriodStart.toISOString().slice(0, 10);
            if (observationDates.includes(pubDateString)) {
                const observation = observations[pubDateString];

                Object.keys(forecast.forecastValues).forEach((key) => {
                    forecasts[i].forecastValues[key] += observation;
                });

                // Otherwise, remove the foreast, it can't be used in the plot
            } else {
                forecasts = forecasts.toSpliced(i, 1);
            }

            // The periods align, no mixed past needed. Just return the forecast as is
        } else if (forecastPeriodStart.getTime() === mixedPastPeriodStart.getTime()) {
            continue;

            // The forecast period is before the mixed past start period, need to remove it!
        } else {
            forecasts = forecasts.toSpliced(i, 1);
        }
    }

    return forecasts;
}
